import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ShowMore from 'react-show-more';
import { noop, remCalc } from '@lux/helpers';

import Spacing from '../Spacing';
import PhoneDetailsPromo from '../PhoneDetailsPromo';
import PhoneColourSelection from '../PhoneColourSelection';
import PhoneSizeSelection from '../PhoneSizeSelection';
import PersistStatusConsumer from '../PersistStatusConsumer';
import htmlStringToReactComponent from '../../helpers/htmlStringToReactComponent';
import { Notification, Divider, Text } from '@dls/web';
import useTranslate from '../../hooks/useTranslation';

const ExpandText = styled.span`
  font-family: inherit;
  font-size: inherit;
  color: ${p => p.theme.colours.link};

  &:hover {
    color: ${p => p.theme.colours.linkVariant};
  }
`;

const ShowMoreWrapper = styled.div`
  display: block;
  font-family: ${p => p.theme.fonts.primary};
  font-weight: normal;
  font-size: ${remCalc(14)};
  line-height: 1.5;
  color: ${p => p.theme.colours.grey_600};
`;

const ErrorMessage = styled.span`
  color: ${p => p.theme.colours.primary};
`;

const PhoneDetails = ({
  title,
  description,
  shippingLiner,
  promotions,
  freeGiftsByDevice,
  freeGiftsBySku,
  freeGiftStockAvail,
  errorMessage,
  colours = [],
  onColourChange = noop,
  onSizeChange = noop,
  selectedColour = '',
  selectedSize = '',
  sizes = [],
  selectedProduct
}) => {
  const { t } = useTranslate();
  const isFreeGiftPresent =
    freeGiftsBySku.filter(gift => gift.colour).length > 0;
  const showPromotions = isFreeGiftPresent || promotions?.primaryLiner;

  // Set Preorder & Backorder Models
  let preOrderText, skuMessage;
  if (selectedProduct?.isPreOrder) {
    skuMessage = t('AVAIL_PREORDER');
    preOrderText = selectedProduct?.preOrderText;
  } else if (selectedProduct?.isBackOrder) {
    skuMessage = t('AVAIL_PREORDER');
    preOrderText = selectedProduct?.backOrderText;
  } else {
    skuMessage = '';
  }

  const isSelectedSizeNotNA = selectedSize !== 'NA';

  return (
    <Fragment>
      <Spacing bottom={0.5}>
        <Text type="bannerTitle">{title}</Text>
      </Spacing>
      <Spacing>
        <ShowMoreWrapper>
          <ShowMore
            lines={2}
            more={<ExpandText>more</ExpandText>}
            less={<ExpandText>less</ExpandText>}
          >
            {description}
          </ShowMore>
        </ShowMoreWrapper>
      </Spacing>
      {showPromotions && (
        <PhoneDetailsPromo
          phoneSizes={sizes}
          phoneColours={colours}
          freeGiftsByDevice={freeGiftsByDevice}
          freeGiftsBySku={freeGiftsBySku}
          freeGiftStockAvail={freeGiftStockAvail}
          promotions={promotions}
          shippingLiner={shippingLiner}
        />
      )}
      <Spacing top={4} topMd={4}>
        <PersistStatusConsumer>
          <Spacing bottom={4} bottomMd={4}>
            <PhoneColourSelection
              selectedColour={selectedColour}
              colours={colours}
              onChange={onColourChange}
            />
            {selectedColour && (
              <Spacing top={1}>
                <ErrorMessage>{errorMessage}</ErrorMessage>
              </Spacing>
            )}
          </Spacing>
        </PersistStatusConsumer>
        <Divider />
        {isSelectedSizeNotNA ? (
          <>
            <Spacing top={4} topMd={4} bottom={4} bottomMd={4}>
              <PhoneSizeSelection
                errorMessage={errorMessage}
                skuMessage={skuMessage}
                selectedSize={selectedSize}
                sizes={sizes}
                onChange={onSizeChange}
              />
            </Spacing>
            <Divider />
          </>
        ) : null}
        {preOrderText &&
        (selectedProduct?.isBackOrder || selectedProduct?.isPreOrder) ? (
          <>
            <Spacing top={6} bottom={6} bottomMd={8}>
              <Notification
                type="info"
                fullWidth={true}
                content={htmlStringToReactComponent(preOrderText)}
              />
            </Spacing>
            <Divider />
          </>
        ) : null}
      </Spacing>
    </Fragment>
  );
};

PhoneDetails.defaultProps = {
  title: '',
  description: '',
  shippingLiner: '',
  errorMessage: '',
  freeGiftsByDevice: [],
  freeGiftsBySku: [],
  freeGiftStockAvail: []
};

PhoneDetails.propTypes = {
  /** Title of phone */
  title: PropTypes.string,
  /** Description of phone */
  description: PropTypes.string,
  /** Shipping Liner of product */
  shippingLiner: PropTypes.string,
  /** Error message */
  errorMessage: PropTypes.string,
  /** Sets the promotion object */
  promotions: PropTypes.object,
  /** Free gifts sorted by device */
  freeGiftsByDevice: PropTypes.array,
  /** Free gifts sorted by sku */
  freeGiftsBySku: PropTypes.array,
  /** Free gifts stock availability*/
  freeGiftStockAvail: PropTypes.array,
  /** Selected capacity size */
  selectedSize: PropTypes.string,
  /** Device sizes */
  sizes: PropTypes.array,
  /** Selected colour name */
  selectedColour: PropTypes.string,
  /** List of colours to render */
  colours: PropTypes.arrayOf(
    PropTypes.shape({
      hexCode: PropTypes.string,
      name: PropTypes.string,
      active: PropTypes.bool
    })
  ),
  onColourChange: PropTypes.func,
  onSizeChange: PropTypes.func
};

export default PhoneDetails;
